import { addDays, weekdayName } from '@app/shared'
import type { ISODate, LearningStreak } from '@app/shared'

import { durationPhrase } from './derive'

// ============================================================================
// THE SEVEN-DAY SESSION STRIP — docs/design/handoff/"LifeOS Learning.dc.html", option 9a.
//
// Minutes per day for the last seven days, both tracks together, bars scaled against the week's
// own peak. White card, radius 16, padding 16/20.
//
// A DAY WITH NOTHING LOGGED KEEPS ITS COLUMN and shows a flat `fill` rule with a faint `0` above
// it. The gaps are the reason the strip is worth drawing: a chart that quietly dropped Sunday
// would make the week look unbroken when it was not.
//
// TODAY IS DRAWN AS AN OUTLINE WHILE IT IS STILL OPEN — dashed, with an em dash instead of a
// number — because a day is not over. Once minutes are logged it becomes a real bar like any
// other; the mockup happens to draw it open, this draws whichever it actually is.
// ============================================================================

const DAYS = 7
const HEIGHT = 152

export function SessionStrip({ streak, today }: { streak: LearningStreak; today: ISODate }) {
  const byDate = new Map(streak.days.map((d) => [d.date, d.minutes]))
  const days = Array.from({ length: DAYS }, (_, i) => {
    const date = addDays(today, i - (DAYS - 1))
    return { date, minutes: byDate.get(date) ?? 0 }
  })
  const peak = Math.max(1, ...days.map((d) => d.minutes))
  const total = days.reduce((n, d) => n + d.minutes, 0)
  const average = Math.round(total / DAYS)

  return (
    <section className="flex flex-none flex-col gap-[13px] rounded-context border border-line bg-surface px-5 py-4 shadow-card">
      <header className="flex flex-none items-center gap-[10px]">
        <h2 className="text-[12.5px] font-semibold leading-none">Session log</h2>
        <span className="text-micro font-normal text-microcopy">last 7 days · minutes per day</span>
        <span className="ml-auto text-micro font-normal text-muted">
          {durationPhrase(total)} total · {average} min average
        </span>
      </header>

      <div className="flex flex-none items-end gap-[14px]" style={{ height: `${HEIGHT}px` }}>
        {days.map((day) => {
          const open = day.date === today && day.minutes === 0
          const share = day.minutes / peak
          return (
            <div
              key={day.date}
              className="flex h-full flex-1 flex-col items-center justify-end gap-2"
              title={`${weekdayName(day.date, true)} ${day.date} · ${day.minutes} min`}
            >
              <span
                className={[
                  'text-micro font-semibold leading-none',
                  day.minutes === 0 ? 'text-faintest-2' : 'text-muted',
                ].join(' ')}
              >
                {open ? '—' : day.minutes}
              </span>
              {open ? (
                <span
                  className="w-full rounded-t-chip border-[1.5px] border-dashed border-teal-100"
                  style={{ height: '26%' }}
                  aria-hidden
                />
              ) : (
                <span
                  className={['w-full', day.minutes === 0 ? 'rounded-[2px] bg-fill' : `rounded-t-chip ${barFill(share)}`].join(' ')}
                  style={{ height: day.minutes === 0 ? '3px' : `${Math.max(6, Math.round(share * 100))}%` }}
                  aria-hidden
                />
              )}
              <span
                className={[
                  'text-[10.5px] leading-none',
                  day.date === today ? 'font-semibold text-teal' : 'font-normal text-microcopy',
                ].join(' ')}
              >
                {day.date === today ? 'Today' : weekdayName(day.date, true)}
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}

/** Four teal steps against the week's own peak — a personal baseline, not an absolute scale. */
function barFill(share: number): string {
  if (share > 0.9) return 'bg-learning'
  if (share > 0.7) return 'bg-teal-300'
  if (share > 0.45) return 'bg-teal-200'
  return 'bg-teal-100'
}

export function SessionStripSkeleton() {
  return (
    <section className="flex flex-none flex-col gap-[13px] rounded-context border border-line bg-surface px-5 py-4 shadow-card">
      <span className="sr-only">Loading…</span>
      <div className="h-[13px] w-[220px] rounded-full bg-fill" aria-hidden />
      <div className="flex items-end gap-[14px]" style={{ height: `${HEIGHT}px` }} aria-hidden>
        {Array.from({ length: DAYS }, (_, i) => (
          <span key={i} className="flex-1 rounded-t-chip bg-fill" style={{ height: '40%' }} />
        ))}
      </div>
    </section>
  )
}
